import { useEffect, useReducer } from 'react'
import { isExternalDomain, isFileDownloadUrl } from 'utils'
import { buildAriaLabel, getCachedTitle, scheduleLinkTitle, subscribeLinkTitles } from 'utils/a11y'

/**
 * Hook che compone l'aria-label per i componenti CTA core.
 *
 * Regole (accessibilità_buttons_PLAN.md):
 *  - Se ariaLabel esplicito → restituirlo invariato
 *  - Se manca ariaLabel, c'è label e href interno → fetch titolo destinazione
 *    e restituire "label - titoloDestinazione" (o solo "label" se non trovato)
 *  - Se non c'è label → undefined (nessun fallback artificiale)
 */
export default function useAriaLabel(
  ariaLabel: string | undefined,
  label: string | undefined,
  href?: string | null
): string | undefined {
  const normalizedAriaLabel = ariaLabel?.trim()
  const normalizedLabel = label?.trim()
  const ariaEqualsLabel =
    !!normalizedAriaLabel && !!normalizedLabel && normalizedAriaLabel === normalizedLabel

  // Se ariaLabel è davvero esplicito non tocchiamo nulla.
  // Se invece coincide con la label, lo trattiamo come fallback arricchibile.
  const shouldResolve =
    (!!normalizedLabel &&
      (!normalizedAriaLabel || ariaEqualsLabel) &&
      !!href &&
      !isExternalDomain(href) &&
      !isFileDownloadUrl(href))

  const [, rerender] = useReducer((n: number) => n + 1, 0)

  useEffect(() => {
    const unsubscribe = subscribeLinkTitles(rerender)
    return () => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!shouldResolve || !href) return
    scheduleLinkTitle(href)
  }, [shouldResolve, href])

  if (!shouldResolve || !href) {
    return buildAriaLabel(ariaLabel, label, null)
  }

  const title = getCachedTitle(href)
  return buildAriaLabel(ariaLabel, label, title ?? null)
}
